import { useEffect } from "react";
import styles from "./DeadlineModal.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";

interface DeadlineModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * The deadline notice, expanded.
 *
 * The hero's "Applications open · Deadline …" line is a one-line summary of a
 * decision that needs explaining — why the date moved, and who it was moved for.
 * That reasoning does not belong in the hero, so the line opens this instead.
 *
 * Shares StoryModal's shell deliberately (same overlay, same rise-in, same
 * Escape/scroll-lock behaviour) so the page has one dialog idiom rather than
 * two. It is a light panel rather than that one's dark lightbox because this is
 * prose to be read, not a video to be watched.
 */
const DeadlineModal = ({ open, onClose }: DeadlineModalProps) => {
  const { t } = useTranslationHook();
  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);

    // Stop the page scrolling behind the dialog, restoring whatever was set.
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="scholarship-deadline-title"
      onClick={onClose}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label={t("scholarship.v2.deadline.close")}
        >
          <span className={styles.closeIcon} aria-hidden="true" />
        </button>

        <p className={styles.eyebrow}>{t("scholarship.v2.deadline.eyebrow")}</p>
        <h2 className={styles.title} id="scholarship-deadline-title">
          {t("scholarship.v2.deadline.title")}
        </h2>

        <p className={styles.body}>{t("scholarship.v2.deadline.body")}</p>
      </div>
    </div>
  );
};

export default wrapperHOC(DeadlineModal, {
  componentName: "DeadlineModal-ScholarshipV2",
  showForChina: true,
});
